import { ArrowUpRight, ArrowDownRight, Quote } from 'lucide-react';
import {
  LineChart, Line, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell,
} from 'recharts';
import { useClientSettings } from '../client-pwa/settings';

const revenueTrend = [
  { month: 'Oct', revenue: 22400, profit: 3800 },
  { month: 'Nov', revenue: 24100, profit: 4200 },
  { month: 'Dec', revenue: 28900, profit: 5400 },
  { month: 'Jan', revenue: 21200, profit: 3100 },
  { month: 'Feb', revenue: 25600, profit: 4500 },
  { month: 'Mar', revenue: 27800, profit: 5100 },
];

const expenses = [
  { name: 'Wages & Super', amount: 142000 },
  { name: 'Rent', amount: 36000 },
  { name: 'Subscriptions', amount: 18400 },
  { name: 'Motor Vehicle', amount: 12200 },
  { name: 'Marketing', amount: 9800 },
];

const revenueMix = [
  { name: 'Residential', value: 142000, color: '#0f766e' },
  { name: 'Commercial', value: 88000, color: '#475569' },
  { name: 'Repairs', value: 38500, color: '#a8a29e' },
  { name: 'Other', value: 16000, color: '#d6d3d1' },
];

const benchmarks = [
  { metric: 'Profit margin', you: 17.0, industry: 12.4 },
  { metric: 'Wages / revenue', you: 49.8, industry: 54.1 },
  { metric: 'Rent / revenue', you: 12.6, industry: 14.8 },
];

export default function InsightsPageV3() {
  const { themeMode } = useClientSettings();
  const isDark = themeMode === 'dark';
  const tooltipStyle = {
    borderRadius: 0,
    border: `1px solid ${isDark ? '#292524' : '#e7e5e4'}`,
    background: isDark ? '#0c0a09' : '#fff',
    fontSize: 11,
    color: isDark ? '#f5f5f4' : '#1c1917',
    fontFamily: 'monospace',
  };
  const pieStroke = isDark ? '#1c1917' : '#fff';
  const axisStroke = isDark ? '#44403c' : '#d6d3d1';
  const axisTickColor = isDark ? '#a8a29e' : '#78716c';
  const profitDashStroke = isDark ? '#e7e5e4' : '#1c1917';

  return (
    <div className="space-y-8 pt-6">
      {/* Editorial header */}
      <div>
        <p className="font-mono text-[12px] font-bold uppercase tracking-[0.25em] text-stone-500">§ 03 · Analysis</p>
        <h2 className="mt-2 font-serif text-3xl font-bold leading-tight tracking-tight">The Quarterly Reading</h2>
        <p className="mt-1 text-[12px] uppercase tracking-wider text-stone-500">By the numbers · Mar quarter, FY 2025-26</p>
      </div>

      {/* Pull-quote headline */}
      <article className="border-y-2 border-stone-900 dark:border-stone-100 py-5">
        <div className="flex items-start gap-3">
          <Quote className="h-5 w-5 flex-shrink-0 text-teal-700 dark:text-teal-400" />
          <div>
            <h3 className="font-serif text-2xl font-bold leading-tight tracking-tight text-stone-900 dark:text-stone-100">
              Profit margin runs <span className="text-teal-700 dark:text-teal-400">37% above</span> the industry standard, driven by a stronger residential mix and disciplined cost management.
            </h3>
            <p className="mt-3 text-[13px] font-bold uppercase tracking-[0.2em] text-stone-500">
              - Lead finding · this period
            </p>
          </div>
        </div>
      </article>

      {/* SECTION: Revenue trend */}
      <section>
        <header className="border-b border-stone-300 dark:border-stone-700 pb-2">
          <p className="font-mono text-[12px] font-bold uppercase tracking-[0.2em] text-teal-700 dark:text-teal-400">Figure 1</p>
          <h3 className="mt-1 font-serif text-xl font-bold tracking-tight">Revenue and profit, six months</h3>
        </header>
        <div className="mt-4 bg-white dark:bg-stone-900 p-1 border border-stone-200 dark:border-stone-800">
          <div className="h-44 p-2">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revenueTrend}>
                <XAxis dataKey="month" axisLine={{ stroke: axisStroke }} tickLine={false} tick={{ fontSize: 10, fill: axisTickColor }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: axisTickColor }} tickFormatter={(v: number) => `$${v / 1000}k`} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => `$${v.toLocaleString()}`} />
                <Line type="monotone" dataKey="revenue" stroke="#0f766e" strokeWidth={2} dot={{ r: 3, fill: '#0f766e' }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="profit" stroke={profitDashStroke} strokeWidth={1.5} strokeDasharray="3 3" dot={{ r: 2, fill: profitDashStroke }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-4 text-[12px] uppercase tracking-wider text-stone-500">
          <span className="flex items-center gap-1.5"><span className="h-0.5 w-3 bg-teal-700 dark:bg-teal-500" /> Revenue</span>
          <span className="flex items-center gap-1.5"><span className="h-0.5 w-3 border-t border-dashed border-stone-900 dark:border-stone-100" /> Profit</span>
        </div>
        <p className="mt-2 font-serif text-[13px] italic text-stone-500">
          Revenue rose 24% over the period; profit moved in line, indicating consistent margin discipline.
        </p>
      </section>

      {/* SECTION: Mix + Cashflow side-by-side */}
      <section className="grid grid-cols-2 gap-3">
        <div>
          <header className="border-b border-stone-300 dark:border-stone-700 pb-2">
            <p className="font-mono text-[12px] font-bold uppercase tracking-[0.2em] text-teal-700 dark:text-teal-400">Figure 2</p>
            <h3 className="mt-1 font-serif text-base font-bold tracking-tight">Revenue mix</h3>
          </header>
          <div className="mt-3 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 p-2">
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={revenueMix} dataKey="value" innerRadius={32} outerRadius={50} paddingAngle={1} stroke={pieStroke} strokeWidth={2}>
                    {revenueMix.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => `$${v.toLocaleString()}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="mt-2 space-y-1">
            {revenueMix.map(r => {
              const total = revenueMix.reduce((s, x) => s + x.value, 0);
              const pct = Math.round((r.value / total) * 100);
              return (
                <div key={r.name} className="flex items-center justify-between text-[12px]">
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2" style={{ background: r.color }} />
                    <span className="text-stone-700 dark:text-stone-300">{r.name}</span>
                  </span>
                  <span className="font-mono font-bold tabular-nums text-stone-900 dark:text-stone-100">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <header className="border-b border-stone-300 dark:border-stone-700 pb-2">
            <p className="font-mono text-[12px] font-bold uppercase tracking-[0.2em] text-teal-700 dark:text-teal-400">Figure 3</p>
            <h3 className="mt-1 font-serif text-base font-bold tracking-tight">Cashflow score</h3>
          </header>
          <div className="mt-3 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 p-2">
            {/* h-32 matches Figure 2's chart height exactly */}
            <div className="flex h-32 flex-col items-center justify-center">
              <p className="font-serif text-5xl font-bold tabular-nums leading-none text-stone-900 dark:text-stone-100">72</p>
              <p className="mt-2 font-mono text-[11px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Healthy</p>
              <div className="mt-3 h-1.5 w-full overflow-hidden bg-stone-100 dark:bg-stone-800">
                <div className="h-full bg-teal-700 dark:bg-teal-500" style={{ width: '72%' }} />
              </div>
            </div>
          </div>
          <div className="mt-2 space-y-1 text-[12px]">
            <div className="flex items-center justify-between"><span className="text-stone-600 dark:text-stone-400">Days runway</span><span className="font-mono font-bold tabular-nums">94</span></div>
            <div className="flex items-center justify-between"><span className="text-stone-600 dark:text-stone-400">Receivables</span><span className="font-mono font-bold tabular-nums">$18,420</span></div>
            <div className="flex items-center justify-between"><span className="text-stone-600 dark:text-stone-400">Bills (30d)</span><span className="font-mono font-bold tabular-nums">$9,810</span></div>
          </div>
        </div>
      </section>

      {/* SECTION: Top expenses */}
      <section>
        <header className="border-b border-stone-300 dark:border-stone-700 pb-2">
          <p className="font-mono text-[12px] font-bold uppercase tracking-[0.2em] text-teal-700 dark:text-teal-400">Table 1</p>
          <h3 className="mt-1 font-serif text-xl font-bold tracking-tight">Where the money flows</h3>
          <p className="mt-1 text-[12px] uppercase tracking-wider text-stone-500">Top expense categories · year to date</p>
        </header>
        <div className="mt-3 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 p-2">
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={expenses} layout="vertical" margin={{ left: 0 }}>
                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: axisTickColor }} tickFormatter={(v: number) => `$${v / 1000}k`} />
                <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: isDark ? '#f5f5f4' : '#1c1917' }} width={110} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => `$${v.toLocaleString()}`} cursor={{ fill: isDark ? '#1c1917' : '#f5f5f4' }} />
                <Bar dataKey="amount" fill="#0f766e" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* SECTION: Editor's note - deductions */}
      <section className="border-l-4 border-teal-700 dark:border-teal-500 bg-white dark:bg-stone-900 px-5 py-5">
        <p className="font-mono text-[12px] font-bold uppercase tracking-[0.25em] text-teal-700 dark:text-teal-400">Editor's note</p>
        <h3 className="mt-2 font-serif text-xl font-bold leading-snug tracking-tight">Three deductions worth a conversation</h3>
        <ol className="mt-4 space-y-4">
          {[
            { t: 'Home office expenses', d: 'A pro-rata share of internet, electricity and phone bills may be claimable. We can model the diary-method workings together.' },
            { t: 'Vehicle logbook', d: 'Twelve weeks of records this quarter would establish business-use percentage for the next five years.' },
            { t: 'Instant asset write-off', d: 'Equipment purchases under $20,000 may be immediately deductible - worth reviewing before EOFY.' },
          ].map((tip, i) => (
            <li key={i} className="border-t border-stone-200 dark:border-stone-800 pt-3">
              <div className="flex items-baseline gap-3">
                <span className="font-mono text-[13px] font-bold tabular-nums text-stone-400 dark:text-stone-500">{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <p className="font-serif text-base font-bold tracking-tight">{tip.t}</p>
                  <p className="mt-1 text-[14px] leading-relaxed text-stone-600 dark:text-stone-400">{tip.d}</p>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* SECTION: Benchmarks table */}
      <section>
        <header className="border-b border-stone-300 dark:border-stone-700 pb-2">
          <p className="font-mono text-[12px] font-bold uppercase tracking-[0.2em] text-teal-700 dark:text-teal-400">Table 2</p>
          <h3 className="mt-1 font-serif text-xl font-bold tracking-tight">Against industry standard</h3>
        </header>
        <table className="mt-3 w-full border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
          <thead>
            <tr className="border-b border-stone-200 dark:border-stone-800 bg-stone-100 dark:bg-stone-800">
              <th className="px-3 py-2 text-left font-mono text-[11px] font-bold uppercase tracking-wider text-stone-500">Metric</th>
              <th className="px-3 py-2 text-right font-mono text-[11px] font-bold uppercase tracking-wider text-stone-500">You</th>
              <th className="px-3 py-2 text-right font-mono text-[11px] font-bold uppercase tracking-wider text-stone-500">Industry</th>
              <th className="px-3 py-2 text-right font-mono text-[11px] font-bold uppercase tracking-wider text-stone-500">Variance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-200 dark:divide-stone-800">
            {benchmarks.map(b => {
              const variance = +(b.you - b.industry).toFixed(1);
              const better = b.metric === 'Profit margin' ? variance > 0 : variance < 0;
              return (
                <tr key={b.metric}>
                  <td className="px-3 py-3 text-sm font-medium text-stone-900 dark:text-stone-100">{b.metric}</td>
                  <td className="px-3 py-3 text-right font-mono text-sm font-bold tabular-nums text-stone-900 dark:text-stone-100">{b.you}%</td>
                  <td className="px-3 py-3 text-right font-mono text-sm tabular-nums text-stone-500">{b.industry}%</td>
                  <td className="px-3 py-3 text-right">
                    <span className={`inline-flex items-center gap-0.5 font-mono text-[13px] font-bold tabular-nums ${better ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>
                      {better ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                      {variance > 0 ? '+' : ''}{variance}pp
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="mt-2 font-serif text-[13px] italic text-stone-500">
          Variance shown in percentage points (pp). Industry benchmark sourced from ATO small-business data.
        </p>
      </section>
    </div>
  );
}
